import { DecisionTree } from '../models/decisionTree';

// 决策记录接口
export interface DecisionRecord {
  id: string;
  timestamp: string;
  question: string;
  decisionTree: DecisionTree;
  tags?: string[];
  userId?: string; // 用于多用户支持
  syncStatus?: 'synced' | 'pending' | 'failed';
  lastSyncTime?: string;
}

// 同步配置接口
export interface SyncConfig {
  enabled: boolean;
  workerUrl: string;
  apiKey: string;
  userId: string;
  autoSync: boolean;
  syncInterval: number; // 自动同步间隔（分钟）
  lastSyncTime?: string;
}

// 默认同步配置
export const defaultSyncConfig: SyncConfig = {
  enabled: false,
  workerUrl: '',
  apiKey: '',
  userId: '',
  autoSync: false,
  syncInterval: 60, // 默认1小时同步一次
};

class CloudSyncService {
  private syncConfig: SyncConfig;

  constructor() {
    // 从localStorage加载同步配置
    this.syncConfig = this.loadSyncConfig();
  }

  // 加载同步配置
  private loadSyncConfig(): SyncConfig {
    try {
      const configJson = localStorage.getItem('cloudSyncConfig');
      if (configJson) {
        return JSON.parse(configJson);
      }
    } catch (error) {
      console.error('加载云同步配置失败:', error);
    }
    return { ...defaultSyncConfig };
  }

  // 保存同步配置
  public saveSyncConfig(config: Partial<SyncConfig>): void {
    this.syncConfig = { ...this.syncConfig, ...config };
    try {
      localStorage.setItem('cloudSyncConfig', JSON.stringify(this.syncConfig));
    } catch (error) {
      console.error('保存云同步配置失败:', error);
    }
  }

  // 获取当前同步配置
  public getSyncConfig(): SyncConfig {
    return { ...this.syncConfig };
  }

  // 检查同步是否已启用
  public isSyncEnabled(): boolean {
    return this.syncConfig.enabled && 
           !!this.syncConfig.workerUrl && 
           !!this.syncConfig.apiKey && 
           !!this.syncConfig.userId;
  }

  // 上传决策记录到Cloudflare Worker
  public async uploadDecisionRecords(records: DecisionRecord[]): Promise<{
    success: boolean;
    message: string;
    syncedCount?: number;
  }> {
    if (!this.isSyncEnabled()) {
      return { success: false, message: '云同步未启用或配置不完整' };
    }

    try {
      // 添加用户ID到每个记录
      const recordsWithUser = records.map(record => ({
        ...record,
        userId: this.syncConfig.userId,
        syncStatus: 'pending' as 'pending',
      }));

      // 构造请求
      const response = await fetch(`${this.syncConfig.workerUrl}/sync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': this.syncConfig.apiKey,
        },
        body: JSON.stringify({ records: recordsWithUser }),
      });

      // 检查响应
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`上传失败: ${response.status} - ${errorText}`);
      }

      const result = await response.json();

      // 更新最后同步时间
      this.saveSyncConfig({
        lastSyncTime: new Date().toISOString()
      });

      return {
        success: true,
        message: '云同步成功',
        syncedCount: result.syncedCount || records.length
      };
    } catch (error) {
      console.error('云同步失败:', error);
      return {
        success: false,
        message: `云同步失败: ${error instanceof Error ? error.message : String(error)}`
      };
    }
  }

  // 从Cloudflare Worker下载决策记录
  public async downloadDecisionRecords(): Promise<{
    success: boolean;
    message: string;
    records?: DecisionRecord[];
  }> {
    if (!this.isSyncEnabled()) {
      return { success: false, message: '云同步未启用或配置不完整' };
    }

    try {
      // 构造请求
      const response = await fetch(`${this.syncConfig.workerUrl}/records/${this.syncConfig.userId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': this.syncConfig.apiKey,
        },
      });

      // 检查响应
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`下载失败: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      
      // 更新最后同步时间
      this.saveSyncConfig({
        lastSyncTime: new Date().toISOString()
      });

      return {
        success: true,
        message: '下载决策记录成功',
        records: result.records || []
      };
    } catch (error) {
      console.error('下载决策记录失败:', error);
      return {
        success: false,
        message: `下载决策记录失败: ${error instanceof Error ? error.message : String(error)}`
      };
    }
  }

  // 执行双向同步
  public async syncRecords(): Promise<{
    success: boolean;
    message: string;
    uploadedCount?: number;
    downloadedCount?: number;
  }> {
    if (!this.isSyncEnabled()) {
      return { success: false, message: '云同步未启用或配置不完整' };
    }

    try {
      // 先获取本地记录
      const localRecordsJson = localStorage.getItem('decisionRecords');
      const localRecords: DecisionRecord[] = localRecordsJson ? JSON.parse(localRecordsJson) : [];
      
      // 上传本地记录
      const uploadResult = await this.uploadDecisionRecords(localRecords);
      
      // 下载云端记录
      const downloadResult = await this.downloadDecisionRecords();
      
      if (!downloadResult.success) {
        return {
          success: false,
          message: downloadResult.message,
          uploadedCount: uploadResult.success ? uploadResult.syncedCount : 0,
        };
      }
      
      // 合并本地和云端记录
      if (downloadResult.records && downloadResult.records.length > 0) {
        const mergedRecords = this.mergeRecords(localRecords, downloadResult.records);
        
        // 保存回本地
        localStorage.setItem('decisionRecords', JSON.stringify(mergedRecords));
      }
      
      // 更新最后同步时间
      this.saveSyncConfig({
        lastSyncTime: new Date().toISOString()
      });
      
      return {
        success: true,
        message: '双向同步完成',
        uploadedCount: uploadResult.syncedCount || 0,
        downloadedCount: downloadResult.records?.length || 0
      };
    } catch (error) {
      console.error('双向同步失败:', error);
      return {
        success: false,
        message: `双向同步失败: ${error instanceof Error ? error.message : String(error)}`
      };
    }
  }
  
  // 合并本地和云端记录，避免重复
  private mergeRecords(localRecords: DecisionRecord[], cloudRecords: DecisionRecord[]): DecisionRecord[] {
    // 创建ID映射，以便快速查找
    const recordMap = new Map<string, DecisionRecord>();
    
    // 先添加所有本地记录
    localRecords.forEach(record => {
      recordMap.set(record.id, record);
    });
    
    // 添加或更新云端记录
    cloudRecords.forEach(cloudRecord => {
      const localRecord = recordMap.get(cloudRecord.id);
      
      // 如果本地不存在或云端版本更新，则使用云端记录
      if (!localRecord || new Date(cloudRecord.timestamp) > new Date(localRecord.timestamp)) {
        recordMap.set(cloudRecord.id, {
          ...cloudRecord,
          syncStatus: 'synced',
        });
      }
    });
    
    // 转换回数组并按时间排序
    return Array.from(recordMap.values())
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  }
  
  // 验证Cloudflare Worker连接
  public async testConnection(): Promise<{
    success: boolean;
    message: string;
  }> {
    if (!this.syncConfig.workerUrl || !this.syncConfig.apiKey) {
      return { success: false, message: '请先配置Worker URL和API密钥' };
    }
    
    try {
      const response = await fetch(`${this.syncConfig.workerUrl}/ping`, {
        method: 'GET',
        headers: {
          'X-API-Key': this.syncConfig.apiKey,
        },
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`连接失败: ${response.status} - ${errorText}`);
      }
      
      const result = await response.json();
      
      return {
        success: true,
        message: `连接成功: ${result.message || '服务可用'}`
      };
    } catch (error) {
      return {
        success: false,
        message: `连接失败: ${error instanceof Error ? error.message : String(error)}`
      };
    }
  }
}

export default new CloudSyncService(); 